import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@klickdata/core/auth/src/token/auth.service';
import { ConfigService } from '@klickdata/core/config/src/config.service';
import { RequestBuilderService } from '@klickdata/core/http/src/request/request-builder.service';
import { More, ResponseData } from '@klickdata/core/http/src/responce/responce';
import { MediaService } from '@klickdata/core/media/src/media.service';
import { MessageSent, UserService } from '@klickdata/core/user/src/user.service';
import { Utils } from '@klickdata/core/util';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, first, map, share, switchMap } from 'rxjs/operators';
import { CustomerCategoryService } from './customer-category/customer-category.service';
import { CustomerLicenseService } from './customer-license/customer-license.service';
import { CustomerPublish } from './customer-publish.model';
import { CustomerServiceService } from './customer-service/customer-service.service';
import { Customer, CustomerData, CustomerLanding } from './customer.model';
import { CustomTab } from '@klickdata/core/custom-tab';
import { AppScope } from '@klickdata/core/resource';
// import { ResourceTag, ResourceTagData } from '@klickdata/core/resource';

@Injectable({
    providedIn: 'root',
})
export class CustomerService {
    protected resourceUrl: string;
    protected customerLogoTypeUrl: string;
    protected checkCustomerExistanceUrl: string;
    protected resourceTagUrl: string;
    protected user_id: Observable<number>;
    protected customer_id: Observable<number>;
    private customersMap = new Map<number, Observable<Customer>>();

    /**
     * CustomerService constructor
     */
    constructor(
        protected auth: AuthService,
        protected config: ConfigService,
        protected builder: RequestBuilderService,
        protected serviceService: CustomerServiceService,
        protected userService: UserService,
        protected mediaService: MediaService,
        protected licenseService: CustomerLicenseService,
        protected categoryService: CustomerCategoryService
    ) {
        this.resourceUrl = `${config.config.apiUrl}customers`;
        this.customerLogoTypeUrl = `${config.config.apiUrl}auth/customer/landing`;
        this.resourceTagUrl = `${this.config.config.apiUrl}tags`;
        this.checkCustomerExistanceUrl = `${this.config.config.apiUrl}findByExample`;
        this.user_id = this.auth.getUser().pipe(
            first(),
            map((user) => user.id)
        );

        this.customer_id = this.auth.getCustomer().pipe(
            first(),
            map((customer) => customer.id)
        );
    }

    /**
     * Create a RequestBuilder for fetching customers.
     */
    public getCustomers(query?: string, limit?: number): Observable<Customer[]> {
        return this.builder
            .get<CustomerData[]>(this.resourceUrl)
            .param('query', query)
            .limit(limit)
            .request()
            .pipe(map((res) => this.createCustomers(res)));
    }

    /**
     * Fetch all contacts
     */
    public getCustomersByIds(customer_ids?: number[]): Observable<Customer[]> {
        const request = this.builder.get<CustomerData[]>(this.resourceUrl);
        if (customer_ids?.length) {
            request.param('ids', customer_ids.join(','));
        }
        return request.request().pipe(map((res) => this.createCustomers(res)));
    }

    public getCustomerLogoType(): Observable<CustomerLanding> {
        let logotype_id: number;
        return this.auth.check().pipe(
            first(),
            switchMap((auth) =>
                auth
                    ? this.auth.getCustomer().pipe(
                          first(),
                          switchMap((customer) => {
                              if (
                                  customer?.medias?.logotype?.length &&
                                  customer?.medias?.logotype[0] &&
                                  logotype_id !== customer.medias?.logotype[0]
                              ) {
                                  logotype_id = customer.medias.logotype[0];
                                  return this.mediaService.getMedia(customer.medias.logotype[0]).pipe(
                                      map((media) =>
                                          media
                                              ? ({
                                                    logotype_url: media.url,
                                                    title: customer.name,
                                                    logotype_padding: customer.logotype_padding,
                                                } as CustomerLanding)
                                              : this.getDefaultLogoType()
                                      )
                                  );
                              } else {
                                  return of(this.getDefaultLogoType());
                              }
                          })
                      )
                    : this.builder
                          .get<CustomerLanding>(this.customerLogoTypeUrl)
                          .request()
                          .pipe(map((res) => (res.data?.logotype_url ? res.data : this.getDefaultLogoType())))
            )
        );
    }

    private getDefaultLogoType(): CustomerLanding {
        return {
            title: 'Klickdata',
            logotype_padding: 0,
            logotype_url: 'assets/images/klickdata-logo-small.png',
            footerLogotype: 'assets/images/logo_liggande_gra.svg',
        };
    }

    public getOwnerCustomers(): Observable<Customer[]> {
        return this.builder
            .get<CustomerData[]>(this.resourceUrl)
            .param('owner', this.user_id)
            .request()
            .pipe(map((res) => this.createCustomers(res)));
    }
    public getCustomerTemplates(query?: string): Observable<Customer[]> {
        return this.builder
            .get<CustomerData[]>(this.resourceUrl)
            .param('template', 1)
            .param('query', query)
            .request()
            .pipe(map((res) => this.createCustomers(res)));
    }

    /**
     * Create a new customer from a template.
     */
    public createFromTemplate(templateId: number, data?: CustomerData, eager?: string[]): Observable<Customer> {
        return this.builder
            .post<CustomerData>(this.resourceUrl, { template_id: templateId, ...data })
            .putEager(eager)
            .request()
            .pipe(map((res) => this.createCustomer(res.data)));
    }
    /**
     * Check if a customer name is already taken.
     */
    public checkCustomerExistanceName(term: string): Observable<CustomerData[]> {
        return this.builder
            .post<CustomerData[]>(this.checkCustomerExistanceUrl, { scope_id: 13, name: term })
            .request()
            .pipe(map((res) => res.data));
    }
    /**
     * Create a new customer.
     */
    public create(customer: CustomerData, eager?: string[]): Observable<Customer> {
        return this.builder
            .post<CustomerData>(this.resourceUrl, customer)
            .putEager(eager)
            .request()
            .pipe(map((res) => this.createCustomer(res.data)));
    }

    /**
     * Update a customer
     */
    public update(customer: CustomerData, eager?: string[]): Observable<Customer> {
        return this.builder
            .put<CustomerData>(`${this.resourceUrl}/${customer.id}`, customer)
            .putEager(eager)
            .request()
            .pipe(map((res) => new Customer(res.data)));
    }
    /**
     * Update a customer
     */
    public updateMultipleCustomers(
        ids: number[],
        data: CustomerData,
        eager?: string[]
    ): Observable<{ success: boolean }> {
        return this.builder
            .put<{ success: boolean }>(`${this.resourceUrl}`, { ...data, ids: ids })
            .putEager(eager)
            .request()
            .pipe(map((res) => res.data));
    }

    public download(ids: number[]): Observable<Blob> {
        return this.builder.post<Blob>(`${this.resourceUrl}/download`, { ids }).download();
    }

    /**
     * Send publish request for customer to the server.
     */
    public publish(customer: Customer, publish: CustomerPublish): Observable<MessageSent> {
        return this.builder
            .put<MessageSent>(`${this.resourceUrl}/${customer.id}/publish`, publish)
            .request()
            .pipe(map((res) => res.data));
    }

    public duplicate(customer: Customer, data?: CustomerData): Observable<number[]> {
        return this.builder
            .post<number[]>(`${this.resourceUrl}/${customer.id}/duplicate`, data ? data : null)
            .request()
            .pipe(
                map((res) => res.data),
                catchError((res: HttpErrorResponse) => {
                    return EMPTY;
                })
            );
    }

    /**
     * Delete customer
     */
    public destroy(customer: Customer): Observable<{ success: boolean }> {
        return this.builder
            .delete<{ success: boolean }>(`${this.resourceUrl}/${customer.id}`)
            .request()
            .pipe(map((res) => res.data));
    }

    public checkCustomerExistance(term: string): Observable<any> {
        return this.builder
            .get<{ success: boolean }>(`${this.resourceUrl}/exists/${term}`)
            .request()
            .pipe(
                map((res) => res.data),
                catchError((err) => {
                    return err && err.error && err.error.error ? of(err.error.error.messages.join('/n')) : EMPTY;
                })
            );
    }

    protected createCustomer(customerData: CustomerData): Customer {
        const customer = new Customer(customerData);
        customer.services = this.serviceService.getServices(customer.id);
        customer.manager$ = !Utils.isEmpty(customer.staff?.manager)
            ? this.userService.getUser(customer.staff.manager)
            : EMPTY;
        customer.license = this.licenseService.getLicense(customer.license_type);
        customer.category = this.categoryService.getCategory(customer.category_value);
        customer.administrators = this.userService.getCustomerAdministrators(customer.id);
        if (customer.tags?.length) {
            customer.tags$ = of(customer.tags);
        }

        return customer;
    }
    // protected mapTags(data: ResourceTagData[]): ResourceTag[] {
    //     return data.map((item) => new ResourceTag(item));
    // }
    protected createCustomers(res: ResponseData<CustomerData[]>): Customer[] {
        return res.data.map((data) => this.createCustomer(data));
    }

    public getCustomer(customer_id: number, eager?: string[]): Observable<Customer> {
        let customer = this.customersMap.get(customer_id);
        if (customer) {
            return customer;
        }
        customer = this.fetchCustomer(customer_id, eager).pipe(
            map((res) => res.data),
            share(),
            catchError(() => of(new Customer({ name: 'Anonymous' })))
        );
        this.customersMap.set(customer_id, customer);
        return customer;
    }

    public fetchCustomer(
        customerId: number,
        eager?: string[],
        param?: { [key: string]: any }
    ): Observable<{ data: Customer; more?: More }> {
        if (!customerId) {
            return of({ data: new Customer() });
        }
        return this.builder
            .get<CustomerData>(`${this.resourceUrl}/${customerId}`)
            .putEager(eager)
            .putParam(param)
            .request()
            .pipe(
                map((res) => ({
                    data: this.createCustomer(res.data),
                    more: res.more,
                }))
            );
    }

    public listenToCustomerUpdates(customerId: number): Observable<Customer> {
        return this.auth
            .listenPrivate(`customer.${customerId}`, 'CustomerUpdateEvent')
            .pipe(map((res: ResponseData<CustomerData>) => this.createCustomer(res.data)));
    }
}
